import {useStore} from 'zustand';
import {createStore} from 'zustand/vanilla';

import type {FeatureFlagKey} from '#lit/lib/feature_flags/FeatureFlag.js';
import type {Nullable} from '#types';
import type {Session} from '#types/session.js';
import type {Organization, Team} from '#types/team.js';
import type {User} from '#types/user.js';

export type SessionStore = {
  session: Nullable<Session>;

  // TODO(joshua): deprecate types below in favor for slim session object
  user: Nullable<User>;
  org: Nullable<Organization>;
  team: Nullable<Team>;
  mixpanelProjectId: Nullable<string>;
  mixpanelDistinctId: Nullable<string>;
  flags: Record<string, boolean>;

  isFlagEnabled: (flag: string) => boolean;
  setSession: (session: Nullable<Session>) => void;
  setUser: (user: Nullable<User>) => void;
  setOrg: (org: Nullable<Organization>) => void;
  setTeam: (team: Nullable<Team>) => void;
  setMixpanelProjectId: (mixpanelProjectId: Nullable<string>) => void;
  setMixpanelDistinctId: (mixpanelDistinctId: Nullable<string>) => void;

  // TODO: Improve this type
  setFlags: (flags: Record<string, boolean>) => void;
};

const createSessionStore = () =>
  createStore<SessionStore>((set, get) => ({
    session: null,
    user: null,
    org: null,
    team: null,
    mixpanelProjectId: null,
    mixpanelDistinctId: null,
    flags: {},
    setSession: session => set({session}),
    setUser: user => set({user}),
    setOrg: org => set({org}),
    setTeam: team => set({team}),
    setMixpanelProjectId: mixpanelProjectId => set({mixpanelProjectId}),
    setMixpanelDistinctId: mixpanelDistinctId => set({mixpanelDistinctId}),
    setFlags: flags => set(state => ({flags: {...state.flags, ...flags}})),
    isFlagEnabled: (flag: string) => {
      return get().flags[flag] ?? false;
    },
  }));

export const sessionStore = createSessionStore();

export const useSessionStore = <U = SessionStore>(
  selector?: (state: SessionStore) => U
) => {
  // @ts-expect-error - This works at runtime but TypeScript doesn't like the undefined selector
  return useStore(sessionStore, selector);
};

export const useFeatureFlag = (flag: FeatureFlagKey) => {
  return useSessionStore(state => state.isFlagEnabled(flag));
};

export const useCurrentTeam = () => {
  return useSessionStore(state => state.session?.team ?? state.team);
};

export const useCurrentOrg = () => {
  return useSessionStore(state => state.session?.organization ?? state.org);
};

export const useCurrentUser = () => {
  return useSessionStore(state => state.session?.user ?? state.user);
};
